import React, { useEffect, useState } from "react";
import {
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  TableContainer,
  Paper,
  Box,
  CircularProgress,
} from "@mui/material";
import EmployeeForm from "./EmployeeForm";
import {
  deleteData,
  getEmployeeIdsData,
  createData as createEmployeeData,
  updateData,
} from "../../api/EmployeeApi";
import {
  createData as createUser,
  deleteData as deleteUsser,
  updateData as updateUser,
  getUserListData,
} from "../../api/UsersApi";

import { AddCircleSharp } from "@mui/icons-material";
import { initialUserData, UserData } from "../../types/User";
import { MasterListData } from "../../types/List";

import { toast } from "react-toastify";
import { showConfirmation } from "../../utils/SwalHelper";
import { hasModuleActionPermission } from "../../utils/RolePermissions";
import { AppActionEnum, ModuleEnum } from "../../utils/AppEnum";
import { getData as getGenderData } from "../../api/MasterListDetail";
import { GenericSearch } from "../../components/common/GenericSearch";
import { GenericTable } from "../../components/common/GenericTable";
import { personTableColumns } from "./tables/TableHeader";
import { EmployeeData, initialEmployeeData } from "../../types/Employee";
import { getDataById as getEmployeeById } from "../../api/EmployeeApi";

const EmployeeList: React.FC = () => {
  const [query, setQuery] = useState<string>("");
  const [formData, setFormData] = useState<UserData>();
  const [employee, setEmployee] =
    useState<Partial<EmployeeData>>(initialEmployeeData);
  const [genderData, setGenderData] = useState<MasterListData[]>([]);
  const [listData, setListData] = useState<UserData[]>([]);
  const [employeeIds, setEmployeeIds] = useState<number[]>([]);
  const [open, setOpen] = useState(false);
  const [isEdit, setIsEdit] = useState(false);
  const [loading, setLoading] = useState(true);
  const [viewDetail, setViewDetail] = useState(false);

  const handleSearch = async () => {
    setLoading(true);
    const result = await getUserListData(employeeIds, query);

    setListData(result || []);
    setLoading(false);
  };

  const loadData = async () => {
    setLoading(true);

    const genderResp = await getGenderData();
    if (genderResp.length < 1) {
      toast.error("Failed to load gender");
    } else {
      await setGenderData(genderResp);
    }

    const employeeRest = await getEmployeeIdsData();
    setEmployeeIds(employeeRest);

    setLoading(false);
  };

  const handleAdd = () => {
    setIsEdit(false);
    setFormData(initialUserData);
    setOpen(true);
  };

  const handleEdit = async (data: UserData) => {
    setIsEdit(true);

    await setFormData(data);

    const employeeData = await getEmployeeById(data.id);
    if (employeeData != null) {
      setEmployee(employeeData);
    }
    setOpen(true);
  };

  const handleOnReset = async () => {
    setQuery("");
    setListData([]);
  };

  const handleDetail = async (data: UserData) => {
    setFormData(data);
    setViewDetail(true);
  };

  const handleBack = () => {
    setViewDetail(false);
  };

  const handleDelete = async (id: number) => {
    try {
      const confirmed = await showConfirmation(
        "Are you sure?",
        "Do you want to delete this record permanently?",
        "warning",
        "Yes, delete it!"
      );

      if (confirmed) {
        const respUser = await deleteUsser(id);
        if (!respUser) {
          toast.error("Failed to delete record");
          return;
        }
        const respPerson = await deleteData(id);
        if (respPerson) {
          toast.success("Record deleted successfully");
          await loadData();
        }
      }
    } catch (error: any) {
      console.error("Delete failed:", error);
      toast.error(error.response?.data?.message || "Error deleting record");
    }
  };

  const onSubmit = async (
    form: Partial<UserData>,
    employee: Partial<EmployeeData>
  ) => {
    setIsEdit(false);

    if (form?.id) {
      const resp = await updateUser(form.id, form);
      if (resp == null) {
        toast.error("Update failed.");
        return;
      }

      const empRest = await updateData(form.id, employee);
      if (empRest == null) {
        toast.error("Update failed.");
        return;
      }
      setOpen(false);
      toast.success("Record successfully updated.");
    } else {
      form.roleId = 2;
      const resp = await createUser(form);

      if (resp == null) {
        toast.error("Failed to create record");
        return;
      }

      employee.userId = resp?.id;

      const respData = await createEmployeeData(employee);
      if (respData == null) {
        await deleteUsser(resp.id);
        toast.error("Failed to create record");
      } else {
        toast.success("Record successfully created.");
        setQuery(form.firstName ?? "");
        handleSearch();
        setOpen(false);
        loadData();
      }
    }
  };

  useEffect(() => {
    loadData();
  }, []);

  return (
    <div>
      {!viewDetail && (
        <Box>
          <Box
            sx={{
              display: "flex",
              flexDirection: { xs: "column", md: "row" }, // column on mobile
              alignItems: { xs: "stretch", md: "center" },
              gap: 2,
              width: "100%",
            }}
          >
            {/* GenericSearch */}
            <Box sx={{ flex: { xs: "0 0 100%", md: "0 0 90%" } }}>
              <GenericSearch
                query={query}
                placeholder="search by User No, First Name, Middle Name, Last Name"
                setQuery={setQuery}
                onSearch={handleSearch}
                onReset={() => handleOnReset()}
              />
            </Box>

            {/* Add Button */}
            {hasModuleActionPermission(
              ModuleEnum.Registration,
              AppActionEnum.Create
            ) && (
              <Box sx={{ flex: { xs: "0 0 100%", md: "0 0 8%" } }}>
                <Button
                  fullWidth
                  startIcon={<AddCircleSharp />}
                  onClick={handleAdd}
                  variant="outlined"
                  color="success"
                >
                  Add
                </Button>
              </Box>
            )}
          </Box>

          {loading ? (
            <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
              <CircularProgress />
            </Box>
          ) : (
            <TableContainer component={Paper} sx={{ overflowX: "auto" }}>
              <GenericTable
                data={listData}
                columns={personTableColumns}
                actions={{
                  edit: (row) => handleEdit(row),
                  delete: (row) => handleDelete(row.id),
                  view: (row) => handleDetail(row),
                }}
              />
            </TableContainer>
          )}

          <Dialog
            open={open}
            onClose={(event, reason) => {
              setFormData(initialUserData);
              if (reason === "backdropClick" || reason === "escapeKeyDown")
                return;
              setOpen(false);
            }}
            disableEscapeKeyDown
            maxWidth="md"
            fullWidth
          >
            <DialogTitle>
              {isEdit ? "Edit Employee" : "Add Employee"}
            </DialogTitle>
            <DialogContent>
              <EmployeeForm
                initialData={formData || initialUserData}
                genderData={genderData}
                employee={employee}
                setEmployee={setEmployee}
                onSubmit={onSubmit}
                onClose={() => setOpen(false)}
              />
            </DialogContent>
          </Dialog>
        </Box>
      )}
    </div>
  );
};

export default EmployeeList;
